<template>
  <div class="app-header">
    <div class="app-header__content">
      <!-- Logo -->
      <div class="app-header__logo">
        <img
          :src="activeLogo"
          :alt="logoAlt"
          class="app-header__logo-image"
        />
      </div>

      <!-- Center Search Section -->
      <div v-if="showSearch || $slots.search" class="app-header__search">
        <slot name="search">
          <v-autocomplete
            v-model="searchValue"
            :items="searchItems"
            :loading="searchLoading"
            :search-input.sync="searchQuery"
            :placeholder="searchPlaceholder"
            :item-text="searchItemText"
            :item-value="searchItemValue"
            :filter="filterSearchItems"
            return-object
            hide-details
            dense
            outlined
            clearable
            prepend-inner-icon="mdi-magnify"
            class="app-header__search-input"
            :menu-props="{ maxHeight: 400, contentClass: 'app-header__search-menu' }"
            @input="handleSearchSelect"
            @update:search-input="handleSearchInput"
          >
            <template #no-data>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 grey--text">
                    No pages found
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
            <template #item="{ item }">
              <slot name="search-item" :item="item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    {{ item[searchItemText] || item.text || item.label }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="item.section || item.category" class="text-caption">
                    {{ item.section || item.category }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </slot>
            </template>
          </v-autocomplete>
        </slot>
      </div>

      <!-- Header Actions -->
      <div class="app-header__actions">
        <!-- Product Switcher -->
        <product-switcher
          v-if="showProductSwitcher && products.length > 0"
          :products="products"
          @product-click="handleProductClick"
        />

        <!-- User Menu -->
        <v-menu
          offset-y
          left
          nudge-bottom="8"
          content-class="app-header__user-menu-dropdown"
        >
          <template #activator="{ on, attrs, value }">
            <button
              class="app-header__user-menu-btn"
              aria-label="User menu"
              :aria-haspopup="true"
              :aria-expanded="value ? 'true' : 'false'"
              v-bind="attrs"
              v-on="on"
            >
              <span class="app-header__user-initials" :style="avatarStyle">
                <img
                  v-if="userAvatar && !avatarError"
                  :src="userAvatar"
                  :alt="`Avatar for ${userName}`"
                  @error="avatarError = true"
                />
                <span v-else>{{ userInitials }}</span>
              </span>
              <span class="app-header__user-name">{{ userName }}</span>
              <lucide-icon name="ChevronDown" :size="16" class="app-header__user-chevron" aria-hidden="true" />
            </button>
          </template>
          <v-list dense class="app-header__user-menu-list">
            <template v-for="(item, index) in userMenuItems">
              <v-divider
                v-if="item.divider"
                :key="`divider-${index}`"
                class="app-header__user-menu-divider"
              />
              <v-list-item
                v-else
                :key="index"
                class="app-header__user-menu-item"
                @click="handleUserMenuClick(item)"
              >
                <v-list-item-icon v-if="item.icon" class="mr-3">
                  <lucide-icon :name="item.icon" :size="18" />
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">{{ item.label }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import LucideIcon from '../LucideIcon';
import ProductSwitcher from '../ProductSwitcher';
import type { Product } from '../ProductSwitcher';

export interface UserMenuItem {
  label: string;
  icon?: string;
  action?: string;
  divider?: boolean;
}

export default Vue.extend({
  name: 'AppHeader',

  components: {
    LucideIcon,
    ProductSwitcher
  },

  props: {
    // App branding
    logoSrc: {
      type: String,
      default: ''
    },
    darkLogoSrc: {
      type: String,
      default: ''
    },
    logoAlt: {
      type: String,
      default: 'Syrenis Logo',
      validator: (value: string) => value.length <= 100
    },

    // Product switcher
    showProductSwitcher: {
      type: Boolean,
      default: true
    },
    products: {
      type: Array as () => Product[],
      default: () => []
    },

    // User menu
    userName: {
      type: String,
      default: 'User',
      validator: (value: string) => value.length <= 100
    },
    userAvatar: {
      type: String,
      default: ''
    },
    userAvatarColor: {
      type: String,
      default: '#406A94'
    },
    userMenuItems: {
      type: Array as () => UserMenuItem[],
      default: () => []
    },

    // Theme switcher control
    showThemeSwitcher: {
      type: Boolean,
      default: false
    },

    // Search/typeahead
    showSearch: {
      type: Boolean,
      default: false
    },
    searchItems: {
      type: Array,
      default: () => []
    },
    searchLoading: {
      type: Boolean,
      default: false
    },
    searchPlaceholder: {
      type: String,
      default: 'Search pages...'
    },
    searchItemText: {
      type: String,
      default: 'text'
    },
    searchItemValue: {
      type: String,
      default: 'value'
    }
  },

  data() {
    return {
      avatarError: false,
      isDarkMode: false,
      searchValue: null as any,
      searchQuery: ''
    };
  },

  computed: {
    activeLogo(): string {
      // Return dark logo if in dark mode and dark logo is provided
      if (this.isDarkMode && this.darkLogoSrc) {
        return this.darkLogoSrc;
      }
      return this.logoSrc;
    },

    userInitials(): string {
      return this.userName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    avatarStyle(): { backgroundColor: string } | undefined {
      // Only apply inline style if a custom color is provided (not the default)
      // This allows the CSS variable var(--primary) to handle theme-aware colors
      const defaultColor = '#406A94';
      if (this.userAvatarColor && this.userAvatarColor.toLowerCase() !== defaultColor.toLowerCase()) {
        return { backgroundColor: this.userAvatarColor };
      }
      return undefined;
    }
  },

  mounted() {
    // Force light theme for non-Syrenis Admin users
    if (!this.showThemeSwitcher) {
      this.forceLightTheme();
    }

    // Check initial dark mode state
    this.isDarkMode = document.documentElement.classList.contains('dark');

    // Watch for theme changes via MutationObserver
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          this.isDarkMode = document.documentElement.classList.contains('dark');
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    // Store observer for cleanup
    (this as any)._themeObserver = observer;
  },

  beforeDestroy() {
    // Clean up observer
    if ((this as any)._themeObserver) {
      (this as any)._themeObserver.disconnect();
    }
  },

  methods: {
    handleUserMenuClick(item: UserMenuItem) {
      this.$emit('user-menu-click', item);
      if (item.action) {
        this.$emit(item.action);
      }
    },

    handleThemeChange(theme: 'light' | 'dark') {
      this.$emit('theme-change', theme);
    },

    handleProductClick(product: Product) {
      this.$emit('product-click', product);
    },

    handleSearchInput(query: string) {
      this.$emit('search-input', query);
    },

    handleSearchSelect(item: any) {
      this.$emit('search-select', item);
      // Clear search after selection
      this.$nextTick(() => {
        this.searchQuery = '';
        this.searchValue = null;
      });
    },

    filterSearchItems(item: any, queryText: string): boolean {
      if (!queryText) return true;
      const query = queryText.toLowerCase();
      const text = (item[this.searchItemText] || item.text || item.label || '').toLowerCase();
      const section = (item.section || item.category || '').toLowerCase();
      const keywords = (item.keywords || []).join(' ').toLowerCase();
      return text.includes(query) || section.includes(query) || keywords.includes(query);
    },

    forceLightTheme() {
      // Force light theme for non-Syrenis Admin users
      const root = document.documentElement;
      root.classList.remove('dark');

      // Clear any saved dark theme preference
      try {
        const STORAGE_KEY = 'cassie-theme-preference';
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // localStorage not available, ignore
      }
    }
  }
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/fonts.scss';
@import '@/assets/styles/spacing.scss';

.app-header {
  background-color: #ffffff;
  background-color: var(--card, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  border-bottom-color: var(--border, #e5e7eb);
  padding: $spacing-md $spacing-xl;
  font-family: $font-family-base;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__logo {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__logo-image {
    height: 48px;
    width: auto;
    max-width: 160px;
    object-fit: contain;
    transition: opacity 150ms ease;
  }

  &__search {
    flex: 1;
    display: flex;
    justify-content: center;
    max-width: 480px;
    margin: 0 $spacing-xl;
  }

  &__search-input {
    width: 100%;

    ::v-deep .v-input__slot {
      background-color: #f9fafb !important;
      background-color: var(--muted, #f9fafb) !important;
      border-radius: var(--radius) !important;
      min-height: 40px !important;

      fieldset {
        border-color: transparent !important;
      }

      &:hover fieldset {
        border-color: #d1d5db !important;
        border-color: var(--border, #d1d5db) !important;
      }
    }

    ::v-deep .v-input--is-focused .v-input__slot {
      background-color: #ffffff !important;
      background-color: var(--card, #ffffff) !important;

      fieldset {
        border-color: #406A94 !important;
        border-color: var(--primary, #406A94) !important;
        border-width: 2px !important;
      }
    }

    ::v-deep .v-icon {
      color: #9ca3af !important;
      color: var(--muted-foreground, #9ca3af) !important;
    }

    ::v-deep input {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 14px;

      &::placeholder {
        color: #9ca3af !important;
        color: var(--muted-foreground, #9ca3af) !important;
      }
    }
  }

  &__search-shortcut {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    font-size: 11px;
    font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
    color: #6b7280;
    color: var(--muted-foreground, #6b7280);
    background-color: #f3f4f6;
    background-color: var(--muted, #f3f4f6);
    border-radius: var(--radius-sm);
    border: 1px solid #e5e7eb;
    border-color: var(--border, #e5e7eb);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    flex-shrink: 0;
  }

  &__user-menu-btn {
    display: flex;
    align-items: center;
    gap: $spacing-sm;
    padding: 6px $spacing-md;
    background: none;
    border: none;
    border-radius: $spacing-sm;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background-color: #f3f4f6;
      background-color: var(--background, #f3f4f6);
    }

    &:focus-visible {
      outline: 2px solid #406A94;
      outline: 2px solid var(--primary, #406A94);
      outline-offset: 2px;
    }
  }

  &__user-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #406A94;
    background-color: var(--brand-steel-blue, #406A94);
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    overflow: hidden;
    flex-shrink: 0;

    // Dark mode: use Syrenis red for avatar
    :global(.dark) & {
      background-color: var(--brand-syrenis-red, #ED303E);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__user-name {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    color: var(--muted-foreground, #6b7280);
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__user-chevron {
    color: #6b7280;
    color: var(--muted-foreground, #6b7280);
  }

  &__user-menu-list {
    padding: $spacing-sm !important;
  }

  &__user-menu-item {
    border-radius: var(--radius);
    padding: $spacing-sm $spacing-md !important;
    min-height: 40px !important;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    letter-spacing: -0.006em;
    color: #6b7280 !important;
    color: var(--muted-foreground, #6b7280) !important;
    text-rendering: optimizeLegibility;

    &:hover {
      background-color: #f3f4f6 !important;
      background-color: var(--muted, #f3f4f6) !important;
      color: #1f2937 !important;
      color: var(--secondary-foreground, #1f2937) !important;
    }

    ::v-deep .v-list-item__icon {
      margin-right: $spacing-md !important;
      color: #6b7280;
      color: var(--muted-foreground, #6b7280);
    }

    ::v-deep .v-list-item__title {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
      font-size: 14px !important;
      letter-spacing: -0.006em !important;
      text-rendering: optimizeLegibility !important;
      color: inherit !important;
    }
  }

  &__user-menu-divider {
    margin: $spacing-xs 0 !important;
    border-color: #e5e7eb !important;
    border-color: var(--border, #e5e7eb) !important;
  }
}

// User menu dropdown styling
::v-deep .app-header__user-menu-dropdown {
  border-radius: $spacing-sm !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid #e5e7eb !important;
  border: 1px solid var(--border, #e5e7eb) !important;
  margin-top: $spacing-xs !important;
  background: #ffffff !important;
  background: var(--card, #ffffff) !important;
  z-index: 9999 !important;

  .v-list {
    padding: $spacing-xs !important;
    background: #ffffff !important;
    background: var(--card, #ffffff) !important;
  }
}

.app-header__user-menu-list {
  background: #ffffff !important;
  background: var(--card, #ffffff) !important;
  padding: $spacing-xs !important;
}

// Search menu dropdown styling
.app-header__search-menu {
  border-radius: var(--radius) !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid #e5e7eb !important;
  border: 1px solid var(--border, #e5e7eb) !important;
  margin-top: $spacing-xs !important;
  background: #ffffff !important;
  background: var(--card, #ffffff) !important;

  .v-list {
    padding: $spacing-xs !important;
    background: #ffffff !important;
    background: var(--card, #ffffff) !important;
  }

  .v-list-item {
    border-radius: var(--radius);
    min-height: 44px !important;

    &:hover {
      background-color: #f3f4f6 !important;
      background-color: var(--muted, #f3f4f6) !important;
    }

    .v-list-item__icon {
      margin-right: $spacing-md !important;
      color: #6b7280;
      color: var(--muted-foreground, #6b7280);
    }

    .v-list-item__title {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
      font-size: 14px !important;
      color: #1f2937 !important;
      color: var(--foreground, #1f2937) !important;
    }

    .v-list-item__subtitle {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
      font-size: 12px !important;
      color: #6b7280 !important;
      color: var(--muted-foreground, #6b7280) !important;
    }
  }
}
</style>

<!-- Dark mode overrides (unscoped for global theme changes) -->
<style lang="scss">
@import '@/assets/styles/spacing.scss';

:global(.dark) {
  .app-header {
    background-color: var(--card);
    border-bottom-color: var(--border);

    &__user-menu-btn {
      &:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }
    }

    &__user-name {
      color: var(--muted-foreground);
    }

    &__user-chevron {
      color: var(--muted-foreground);
    }

    &__user-menu-dropdown {
      background: var(--card) !important;
      border-color: var(--border) !important;

      .v-list {
        background: var(--card) !important;
      }
    }

    &__user-menu-list {
      background: var(--card) !important;
    }

    &__user-menu-item {
      color: var(--muted-foreground) !important;

      &:hover {
        background-color: rgba(255, 255, 255, 0.05) !important;
        color: var(--foreground) !important;
      }

      ::v-deep .v-list-item__icon {
        color: var(--muted-foreground);
      }
    }

    &__user-menu-divider {
      border-color: var(--border) !important;
    }
  }
}
</style>
